"use client";

import { IconWorkflowOutline18 } from "nucleo-ui-outline-18";
import { getPreviewVerticalGap, getWorkflowVerticalGap } from "@/lib/workflow/layoutPolicy";
import { Controls, useNodesInitialized, useReactFlow, type Node } from "@xyflow/react";
import type { Timer } from "d3-timer";
import { useCallback, useEffect, useRef } from "react";
import { createWorkflowLayoutPlan, runWorkflowLayoutTransition } from "../_lib/workflowLayoutExecution";
import { useCompactView, useSetCompactView } from "./CompactViewContext";

import { getCompactPreference, setCompactPreference } from "@/lib/workflow/compactPreference";

export { getCompactPreference, setCompactPreference };

/**
 * Returns the vertical gap for layout based on compact preference and preview mode.
 * Preview mode uses tighter spacing since there are no add-node placeholder icons.
 */
export function getVerticalGap(isCompact: boolean, preview = false): number {
  return preview ? getPreviewVerticalGap(isCompact) : getWorkflowVerticalGap(isCompact);
}

/**
 * Shared preview canvas controls for read-only ReactFlow previews.
 * Includes: zoom controls, auto-arrange button, compact view toggle.
 * Used in WorkflowDetailModal and template detail page.
 */
export function PreviewCanvasControls({ className }: { className?: string }) {
  const isCompact = useCompactView();
  const setCompact = useSetCompactView();
  const { getNodes, getNode, setNodes, getEdges, fitView } = useReactFlow();
  const timerRef = useRef<Timer | null>(null);

  const handleLayout = useCallback(() => {
    runWorkflowLayoutTransition({
      isCompact,
      preview: true,
      getNodes,
      getNode,
      getEdges,
      setNodes,
      fitView,
      fitViewOptions: { duration: 200, padding: 0.25 },
      fitViewWhenUnchanged: true,
      timerRef,
    });
  }, [getNodes, getNode, setNodes, getEdges, fitView, isCompact]);

  const handleToggleCompact = useCallback(() => {
    const next = !isCompact;
    setCompact(next);

    // Wait for React to re-render nodes with new compact/expanded content before measuring DOM
    setTimeout(() => {
      requestAnimationFrame(() => {
        runWorkflowLayoutTransition({
          isCompact: next,
          preview: true,
          getNodes,
          getNode,
          getEdges,
          setNodes,
          fitView,
          fitViewOptions: { duration: 200, padding: 0.3 },
          fitViewWhenUnchanged: true,
          timerRef,
          useFreshMeasurements: true,
        });
      });
    }, 150);
  }, [isCompact, getNodes, getNode, setNodes, getEdges, fitView, setCompact]);

  useEffect(() => {
    return () => {
      timerRef.current?.stop();
      timerRef.current = null;
    };
  }, []);

  return (
    <>
      <Controls
        className={
          className ||
          "border-ds-neutral-300 dark:border-ds-neutral-700 [&>button:hover]:bg-ds-neutral-100 dark:[&>button:hover]:bg-ds-neutral-700 [&>button]:border-ds-neutral-300 [&>button]:text-ds-text-secondary dark:[&>button]:bg-ds-neutral-800 bg-ds-surface-card border opacity-0 transition-opacity duration-200 group-hover:opacity-100 [&>button]:bg-white dark:[&>button]:border-zinc-600 dark:[&>button]:text-zinc-300"
        }
        showInteractive={false}
      >
        <button type="button" onClick={handleLayout} title="Auto-arrange nodes" className="react-flow__controls-button">
          <IconWorkflowOutline18 className="h-4 w-4" />
        </button>
      </Controls>
      {/* Compact view toggle - bottom left */}
      <div className="absolute bottom-[15px] left-14 z-[4]" style={{ boxShadow: "0 0 2px 0 #00000014" }}>
        <button
          type="button"
          onClick={handleToggleCompact}
          className="border-ds-neutral-300 text-ds-text-subtle dark:border-ds-neutral-700 dark:hover:bg-ds-neutral-700 bg-ds-surface-card shadow-ds-soft hover:bg-ds-surface-hover flex h-[28px] items-center gap-2 border px-2 text-xs font-medium uppercase opacity-0 transition-all group-hover:opacity-100"
        >
          <div
            className={`relative h-4 w-7 rounded-full transition-colors ${isCompact ? "bg-blue-500" : "bg-ds-neutral-300 dark:bg-zinc-600"}`}
          >
            <div
              className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${isCompact ? "translate-x-3.5" : "translate-x-0.5"}`}
            />
          </div>
          Compact view
        </button>
      </div>
    </>
  );
}

/**
 * Runs auto-arrange after ReactFlow nodes are initialized (measured).
 * Re-runs when `contentKey` changes, so parent re-renders that replace the
 * node set (e.g. template data reloads) recover layout + fitView instead of
 * leaving nodes at their raw unlaid-out positions.
 * Uses the compact-view context for spacing so gap matches rendered node size.
 * Place inside <ReactFlow> as a child component.
 */
export function AutoArrangeOnMount({ contentKey }: { contentKey?: string }) {
  const nodesInitialized = useNodesInitialized();
  const { getNodes, setNodes, getEdges, fitView } = useReactFlow();
  const isCompact = useCompactView();
  const effectiveKey = contentKey ?? "__default__";
  const lastRunKey = useRef<string | null>(null);
  const lastRefitKey = useRef<string | null>(null);

  useEffect(() => {
    if (!nodesInitialized) return;
    if (lastRunKey.current === effectiveKey) return;

    const nodes = getNodes();
    if (nodes.length === 0) return;

    // Check that nodes have measured dimensions
    const nodesAreMeasured = nodes.some((n: Node) => n.measured?.width && n.measured?.height);
    if (!nodesAreMeasured) return;

    lastRunKey.current = effectiveKey;

    const plan = createWorkflowLayoutPlan({
      edges: getEdges(),
      isCompact,
      nodes,
      preview: true,
    });

    if (plan?.hasChanges) {
      setNodes(plan.finalNodes);
    }

    // Wait for React Flow to apply new positions before fitting view.
    // Double rAF ensures the layout paint is complete before fitView calculates the viewport.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fitView({ duration: 300, padding: 0.12, maxZoom: 1, minZoom: 0.1 });
      });
    });
  }, [nodesInitialized, effectiveKey, isCompact, getNodes, setNodes, getEdges, fitView]);

  // Re-run layout once more after nodes resize (e.g. compact mode dimensions settle).
  // nodesInitialized can fire before compact-mode rendering fully measures the shorter nodes.
  useEffect(() => {
    if (!nodesInitialized) return;
    if (lastRefitKey.current === effectiveKey) return;
    // Small delay to let compact mode re-measure node heights
    const timeout = setTimeout(() => {
      const nodes = getNodes();
      if (nodes.length === 0) return;
      lastRefitKey.current = effectiveKey;

      const plan = createWorkflowLayoutPlan({
        edges: getEdges(),
        isCompact,
        nodes,
        preview: true,
        useFreshMeasurements: true,
      });
      if (plan?.hasChanges) {
        setNodes(plan.finalNodes);
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fitView({ duration: 200, padding: 0.12, maxZoom: 1, minZoom: 0.1 });
        });
      });
    }, 150);
    return () => clearTimeout(timeout);
  }, [nodesInitialized, effectiveKey, isCompact, getNodes, setNodes, getEdges, fitView]);

  return null;
}
